import { useTranslation } from 'react-i18next';
import { DataTable, DropdownMenu, type ColumnConfig, type DropdownMenuItem } from '@/components/ui';

export interface AccountsTableProps<Row> {
    rows: Row[];
    columns: ColumnConfig<Row>[];
    rowKey: keyof Row | ((row: Row) => string);
    selectedKeys: string[];
    onSelectedKeysChange: (keys: string[]) => void;
    sortedInfo?: { field: string; order: 'ascend' | 'descend' | null };
    onSortChange?: (ordering: string) => void;
    /** Absent ⇒ no "Xem" button (org screen: no `GET /accounts/{sub}`-style
     *  detail read exists on Assignment rows yet, see `ScopedAccountsTable`). */
    onViewDetail?: (row: Row) => void;
    /** Row-level menu beyond "Xem" — empty array renders no kebab for that row. */
    rowActions: (row: Row) => DropdownMenuItem[];
    rowActionsAriaLabel: (row: Row) => string;
    emptyText: string;
}

/**
 * EAA-1820: the ONE accounts table. Replaces `accounts/AccountsTable.tsx`
 * (realm, already on the kit's `DataTable`) and
 * `hierarchy/scopedAccounts/ScopedAccountsTable.tsx` (org, a hand-rolled
 * `<table>` with its own checkbox/sort-header/status-dot markup) — the org table
 * is migrated onto `DataTable` here rather than the other way around, so both
 * contexts get the same selection chrome, the same sort-caret affordance and the
 * same single scroll region (`shared/AccountsScreen`'s `.acc-table-scroll`
 * wrapper) instead of two hand-tuned equivalents that drift.
 *
 * Column CONTENT stays fully context-owned — each caller builds its own
 * `ColumnConfig<Row>[]` (see `accounts/AccountsTable.tsx` and
 * `hierarchy/scopedAccounts/ScopedAccountsTable.tsx`, now thin column-builders
 * around this component) because the row shapes are genuinely different
 * entities: a Keycloak account vs. an `Assignment` grant. What is shared is the
 * chrome around whichever columns a context provides, plus the "Xem" button +
 * kebab pattern every row gets.
 */
export function AccountsTable<Row extends object>({
    rows,
    columns,
    rowKey,
    selectedKeys,
    onSelectedKeysChange,
    sortedInfo,
    onSortChange,
    onViewDetail,
    rowActions,
    rowActionsAriaLabel,
    emptyText,
}: AccountsTableProps<Row>) {
    const { t } = useTranslation();

    const fullColumns: ColumnConfig<Row>[] = [
        ...columns,
    ];

    return (
        <div className="acc-table-scroll">
            <DataTable<Row>
                className="portal-accounts-table-card"
                data={rows}
                columns={fullColumns}
                rowKey={rowKey}
                pagination={false}
                emptyText={emptyText}
                sortedInfo={sortedInfo}
                onSortChange={onSortChange}
                rowSelection={{
                    selectedRowKeys: selectedKeys,
                    onChange: (keys) => onSelectedKeysChange(keys as string[]),
                }}
                actions={(row) => {
                    const items = rowActions(row);
                    return (
                        <span className="portal-accounts-td-actions">
                            {onViewDetail && (
                                <button
                                    type="button"
                                    className="portal-btn acc-row-view"
                                    onClick={() => onViewDetail(row)}
                                >
                                    {t('console.accounts.table.viewAction')}
                                </button>
                            )}
                            {items.length > 0 && (
                                <DropdownMenu
                                    triggerAriaLabel={rowActionsAriaLabel(row)}
                                    items={items}
                                />
                            )}
                        </span>
                    );
                }}
            />
        </div>
    );
}

export default AccountsTable;
